import MenuIcon from '@mui/icons-material/Menu';
import { Avatar, Box, Collapse, List, Paper, Popper, Stack } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import React, { useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// import ConfirmDialog from '../../../components/ConfirmDialog';
// import useModal from '../../../components/modal/useModal';
import LogoutIcon from '@assets/drawer-icon/LogoutIcon';
import { useMsal } from '@azure/msal-react';
import LanguageSelect from '@components/LanguageSelect';
import PopUpWarning from '@components/PopUpWarning';
import { appConfig } from '@configs/index';
import usePopUp from '@hooks/usePopUp';
import { ChevronLeftRounded } from '@mui/icons-material';
import color from '@theme/Colors';
import i18n from 'src/translations/config';
import ItemDrawer from './DrawerItem';
import { DrawerContext, DrawerItem } from './useDrawer';
import { BiChevronDown, BiChevronRight } from 'react-icons/bi';

const drawerWidth = 200;

type Props = {
    open: (open: boolean) => void;
};

export default function MyDrawer(props: Props) {
    const { instance, accounts } = useMsal();
    const navigate = useNavigate();
    const { t } = i18n;
    const {
        ListDrawItem,
        open,
        handleDrawerClose,
        activeItem,
        setActiveItem,
        handleDrawerOpen,
        openGroups,
        toggleGroup,
    } = useContext(DrawerContext);
    const isShowMultiLanguage = appConfig.flagFeatures.multiLanguage;

    // Popper/Menu state for icon-only (closed) mode
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [activeGroup, setActiveGroup] = useState<DrawerItem | null>(null);
    const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const openSubmenu = (el: HTMLElement, item: DrawerItem) => {
        if (leaveTimer.current) clearTimeout(leaveTimer.current);
        setMenuAnchor(el);
        setActiveGroup(item);
    };

    const scheduleClose = () => {
        leaveTimer.current = setTimeout(() => {
            setMenuAnchor(null);
            setActiveGroup(null);
        }, 300);
    };

    const cancelClose = () => {
        if (leaveTimer.current) clearTimeout(leaveTimer.current);
    };

    const closeSubmenu = () => {
        if (leaveTimer.current) clearTimeout(leaveTimer.current);
        setMenuAnchor(null);
        setActiveGroup(null);
    };

    const popUpWarning = usePopUp({
        onConfirm() {
            const currentAccount = instance.getAccountByHomeId(accounts[0].homeAccountId);
            console.log(currentAccount?.homeAccountId);
            instance.logoutRedirect({ account: currentAccount, postLogoutRedirectUri: '/' });
        },
    });

    return (
        <>
            <PopUpWarning {...popUpWarning} message="Are you sure you want to log out?" />
            <Box sx={{ display: 'flex', pt: 10 }}>
                <Drawer
                    variant="permanent"
                    open={open}
                    sx={{
                        pt: 10,
                        '& .MuiPaper-root': {
                            borderRight: open ? `1px solid ${color.grey100}` : '',
                            background: color.grey100,
                            width: open ? '245px' : '70px',
                            pt: 12,
                            px: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100vh',
                            overflow: 'visible',
                        },
                    }}
                >
                    <DrawerHeader sx={{ padding: 0, justifyContent: 'center' }} className="drawer-header">
                        <Stack direction={'row'}>
                            {isShowMultiLanguage && (
                                <Stack sx={{ display: open ? 'block' : 'none', transition: 'all 1s' }}>
                                    <LanguageSelect isShowLabel={true} />
                                </Stack>
                            )}
                            <Avatar
                                sx={[
                                    { background: color.grey100, color: color.grey600 },
                                    open
                                        ? {
                                              display: 'flex',
                                              width: '30px',
                                              height: '30px',
                                              position: 'absolute',
                                              boxShadow: '4px 2px 8px 2px rgb(0 0 0 / 9%)',
                                              zIndex: 9,
                                              right: '-15px',
                                              top: 110,
                                          }
                                        : {},
                                ]}
                            >
                                {open ? (
                                    <ChevronLeftRounded
                                        sx={{ opacity: 1, cursor: 'pointer', fontSize: 22 }}
                                        onClick={() => {
                                            handleDrawerClose();
                                            props.open(open);
                                        }}
                                    />
                                ) : (
                                    <Box
                                        onClick={() => {
                                            handleDrawerOpen();
                                            props.open(open);
                                        }}
                                    >
                                        <MenuIcon style={{ width: '24px', height: '24px', cursor: 'pointer' }} />
                                    </Box>
                                )}
                            </Avatar>
                        </Stack>
                    </DrawerHeader>

                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                scrollbarWidth: 'thin',
                                scrollbarColor: 'rgba(0,0,0,0.15) transparent',
                                '&::-webkit-scrollbar': { width: 4 },
                                '&::-webkit-scrollbar-track': { background: 'transparent' },
                                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 2 },
                                '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.25)' },
                            }}
                        >
                            <List disablePadding>
                                {ListDrawItem.map((item, index) => {
                                    if (item.children) {
                                        const isGroupActive = item.children.some((c) => c.id === activeItem);

                                        if (open) {
                                            return (
                                                <React.Fragment key={index}>
                                                    <ItemDrawer
                                                        icon={item.icon}
                                                        isActive={isGroupActive}
                                                        label={item.label}
                                                        open={open}
                                                        endIcon={
                                                            openGroups[item.id] ? (
                                                                <BiChevronDown fontSize="large" />
                                                            ) : (
                                                                <BiChevronRight fontSize="large" />
                                                            )
                                                        }
                                                        onClick={() => toggleGroup(item.id)}
                                                    />
                                                    <Collapse in={!!openGroups[item.id]} timeout="auto" unmountOnExit>
                                                        <List disablePadding>
                                                            {item.children.map((child, ci) => (
                                                                <ItemDrawer
                                                                    key={ci}
                                                                    icon={child.icon}
                                                                    isActive={child.id === activeItem}
                                                                    label={child.label}
                                                                    open={open}
                                                                    isSubItem
                                                                    onClick={() => {
                                                                        setActiveItem(child.id);
                                                                        navigate(child.path);
                                                                    }}
                                                                />
                                                            ))}
                                                        </List>
                                                    </Collapse>
                                                </React.Fragment>
                                            );
                                        }

                                        return (
                                            <Box
                                                key={index}
                                                onMouseEnter={(e) => openSubmenu(e.currentTarget, item)}
                                                onMouseLeave={scheduleClose}
                                            >
                                                <ItemDrawer
                                                    icon={item.icon}
                                                    isActive={isGroupActive || activeGroup?.id === item.id}
                                                    label={item.label}
                                                    open={open}
                                                    disableTooltip
                                                    onClick={() => {}}
                                                />
                                            </Box>
                                        );
                                    }

                                    return (
                                        <ItemDrawer
                                            key={index}
                                            icon={item.icon}
                                            isActive={item.id === activeItem}
                                            label={item.label}
                                            open={open}
                                            onClick={() => {
                                                setActiveItem(item.id);
                                                navigate(item.path);
                                            }}
                                        />
                                    );
                                })}
                            </List>
                        </Box>
                        <Box sx={{ mt: 'auto', px: 0, pb: 1 }}>
                            <ItemDrawer
                                key={99}
                                icon={<LogoutIcon className="svg-item-Menu" />}
                                isActive={false}
                                label={t('drawer.logout')}
                                open={open}
                                onClick={popUpWarning.setTrue}
                            />
                        </Box>
                    </Box>
                </Drawer>
                {/* <ConfirmDialog
                open={popUpConfirm.open}
                title={t('dashboard:title.logOut')}
                content={t('dashboard:text.areYouSureWantToLogOut')}
                onClose={() => popUpConfirm.setOpen(false)}
                onConfirm={logout}
            /> */}
            </Box>
            <Popper
                open={Boolean(menuAnchor)}
                anchorEl={menuAnchor}
                placement="auto"
                style={{ zIndex: 1300 }}
                modifiers={[{ name: 'offset', options: { offset: [0, 15] } }]}
            >
                <Paper
                    elevation={4}
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                    sx={{ borderRadius: '8px', minWidth: 180, p: 0.5, overflow: 'hidden' }}
                >
                    <List
                        disablePadding
                        sx={{
                            '& .MuiListItemButton-root': { minHeight: 40, py: 0.25 },
                            '& .MuiTypography-root': { fontSize: 13 },
                            '& .MuiListItemIcon-root svg': { fontSize: 20 },
                        }}
                    >
                        {activeGroup?.children?.map((child, ci) => (
                            <ItemDrawer
                                key={ci}
                                icon={child.icon}
                                isActive={child.id === activeItem}
                                label={child.label}
                                open={true}
                                isSmallDrawer
                                onClick={() => {
                                    setActiveItem(child.id);
                                    navigate(child.path);
                                    closeSubmenu();
                                }}
                            />
                        ))}
                    </List>
                </Paper>
            </Popper>
        </>
    );
}

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

export const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));
